import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@design-system';
import { calculateAge } from '@shared';
import { selectClient, selectClientCalculationSettings } from '@/model';
import {
  filterShortTermCareQuotes,
  ShortTermCareQuoteCard,
  SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS,
  useMaxMonthlyPremium,
  useQuoteSelection,
  useSelectedDailyBenefitAmounts,
  useShortTermCareFiltersActions,
  useShortTermCareInflationOption,
  useShortTermCarePremiumCeiling,
  useShortTermCareQuotes,
  useShortTermCareQuoteProjections,
} from '@/features/shop-insurance';

export interface ShortTermCareShopViewProps {
  advisorId?: string;
  stateAbbreviation?: string;
  clientBirthDate?: string | Date;
  tobacco?: boolean;
}

/**
 * ManhattanLife OmniFlex short-term care, on its own.
 *
 * Selected through the dedicated 'Short-Term Care' payment option, which the
 * shop renders instead of — not alongside — the life and traditional-LTC
 * pipeline. That isolation is the point: STC is day-denominated, single-life
 * only, monthly-billed and priced from imported rate rows, so almost none of
 * the shared shop machinery (buyer tabs, joint policies, benefit design,
 * target-premium slider) applies to it. Keeping it in its own view lets the
 * product iterate without perturbing carriers that already work.
 */
export function ShortTermCareShopView({
  advisorId,
  stateAbbreviation,
  clientBirthDate,
  tobacco,
}: ShortTermCareShopViewProps) {
  // Keyed by quote id rather than list position: a refetch that reorders or
  // resizes the list would otherwise transfer the expanded state to a
  // different card.
  const [expandedQuoteIds, setExpandedQuoteIds] = useState<string[]>([]);
  const selectedDailyBenefitAmounts = useSelectedDailyBenefitAmounts();
  const maxMonthlyPremium = useMaxMonthlyPremium();
  // Server-side, not a display filter: every election is stored under both
  // inflation options, so the endpoint returns one or the other.
  const inflationOption = useShortTermCareInflationOption();
  const { setMaxMonthlyPremium } = useShortTermCareFiltersActions();
  const { isQuoteSelected } = useQuoteSelection({ selectedTab: 'single' });
  const client = useSelector(selectClient);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType || 'total';

  const issueAge = clientBirthDate ? calculateAge(new Date(clientBirthDate)) : undefined;

  const { shortTermCareQuotes, isLoading } = useShortTermCareQuotes({
    advisorId,
    state: stateAbbreviation,
    issueAge,
    // This view IS the monthly short-term care view; the hook's payment-option
    // gate is satisfied by construction rather than by the shop's selector.
    paymentOption: 'Monthly',
    tobacco,
    // Without this the standard and household-discount rate classes both come
    // back and every configuration renders twice, ten percent apart.
    discountKey: '',
    // Pushed into SQL: the daily benefit axis is the widest one, so narrowing
    // it server-side keeps the payload proportional to what is on screen. An
    // empty selection is not sent at all — the repository reads an empty array
    // as "no filter" and would answer with every amount — so the query is
    // suppressed and the list renders empty instead.
    dailyBenefitAmounts: selectedDailyBenefitAmounts.length
      ? selectedDailyBenefitAmounts
      : SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS,
    inflationOption,
    enabled: selectedDailyBenefitAmounts.length > 0,
  });

  // Measured before the premium filter runs, so the ceiling options describe
  // everything on offer rather than what the current ceiling already allows.
  useShortTermCarePremiumCeiling(shortTermCareQuotes);

  const visibleQuotes = useMemo(
    () =>
      filterShortTermCareQuotes(shortTermCareQuotes, {
        selectedDailyBenefitAmounts,
        maxMonthlyPremium,
      }),
    [shortTermCareQuotes, maxMonthlyPremium, selectedDailyBenefitAmounts],
  );

  // Scoped to the filtered list: each projection is a full derivative pass, so
  // projecting everything fetched would pay for cards that are not on screen.
  const projections = useShortTermCareQuoteProjections({
    quotes: visibleQuotes,
    client,
    returnOnInvestmentCalculationType,
  });

  /**
   * Best return first, matching the life and traditional-LTC lists
   * (use-adjusted-quotes, use-traditional-ltc-quotes-with-roi). Sorting happens
   * after projection because ROI is computed here, not in SQL — the query
   * returns rows ordered by benefit period. A quote whose projection failed has
   * no ROI to rank on and sorts last rather than to the top as a zero.
   */
  const sortedQuotes = useMemo(
    () =>
      [...visibleQuotes].sort((quoteA, quoteB) => {
        const roiA = projections[quoteA.shortTermCareQuoteId]?.roi;
        const roiB = projections[quoteB.shortTermCareQuoteId]?.roi;
        if (roiA === undefined) return roiB === undefined ? 0 : 1;
        if (roiB === undefined) return -1;
        return roiB - roiA;
      }),
    [visibleQuotes, projections],
  );

  const handleCardToggle = (quoteId: string) =>
    setExpandedQuoteIds(current =>
      current.includes(quoteId) ? current.filter(id => id !== quoteId) : [...current, quoteId],
    );

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="short-term-care-shop-view"
    >
      {/* The controls live in the sidebar; the count belongs with the results
          it describes. */}
      {!isLoading && (
        <p
          className="text-muted-foreground text-xs"
          data-testid="short-term-care-quote-count"
        >
          {visibleQuotes.length} {visibleQuotes.length === 1 ? 'quote' : 'quotes'}
        </p>
      )}
      {isLoading ? (
        <div
          className="text-muted-foreground p-6 text-sm"
          data-testid="short-term-care-shop-loading"
        >
          Loading short-term care quotes…
        </div>
      ) : visibleQuotes.length === 0 ? (
        <div
          className="text-muted-foreground flex flex-col items-start gap-2 p-6 text-sm"
          data-testid="short-term-care-shop-empty"
        >
          {maxMonthlyPremium === null ? (
            <span>No short-term care quotes match these filters.</span>
          ) : (
            <>
              <span>No short-term care quotes are available under ${maxMonthlyPremium}/mo.</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setMaxMonthlyPremium(null)}
                data-testid="short-term-care-shop-clear-premium-limit"
              >
                Clear premium limit
              </Button>
            </>
          )}
        </div>
      ) : (
        sortedQuotes.map(quote => {
          const { isSelected, fundingSourceId } = isQuoteSelected(quote);

          return (
            <ShortTermCareQuoteCard
              key={quote.shortTermCareQuoteId}
              testId="short-term-care-quote-card"
              quote={quote}
              isExpanded={expandedQuoteIds.includes(quote.shortTermCareQuoteId)}
              onToggleExpand={() => handleCardToggle(quote.shortTermCareQuoteId)}
              isSelected={isSelected}
              fundingSourceId={fundingSourceId}
              projection={projections[quote.shortTermCareQuoteId]}
            />
          );
        })
      )}
    </div>
  );
}
